'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { Users, X, Target, Zap, Calendar, Star, RefreshCw } from 'lucide-react';
import { getFixtureLineups, type ApiLineup } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

interface Player {
  id: string;
  number: number;
  name: string;
  position: string;
  rating?: number;
  hasGoal?: boolean;
  hasYellow?: boolean;
  hasRed?: boolean;
  isSub?: boolean;
  stats?: PlayerStats;
}

interface PlayerStats {
  goals: number;
  assists: number;
  appearances: number;
  matchRating: number;
  minutesPlayed: number;
  shots: number;
  shotsOnTarget: number;
  passes: number;
  passAccuracy: number;
  tackles: number;
  yellowCards: number;
  redCards: number;
}

const POSITION_ORDER: Record<string, number> = {
  G: 0, GK: 0,
  D: 1, CB: 1, RB: 1, LB: 1, RWB: 1, LWB: 1,
  M: 2, CM: 2, CDM: 2, CAM: 2, RM: 2, LM: 2,
  F: 3, ST: 3, CF: 3, LW: 3, RW: 3, SS: 3,
};

function posOrder(pos: string): number {
  return POSITION_ORDER[pos?.toUpperCase()] ?? 2;
}

function mapApiLineupToPlayers(lineup: ApiLineup): Player[] {
  return (lineup.startXI ?? [])
    .map(({ player }) => ({
      id: String(player.id),
      number: player.number,
      name: player.name,
      position: player.pos ?? '',
    }))
    .sort((a, b) => posOrder(a.position) - posOrder(b.position));
}

function StatBadge({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-1 px-3 py-2 rounded-lg" style={{ background: 'hsl(222 25% 14%)' }}>
      <div className="text-slate-400">{icon}</div>
      <span className="text-lg font-bold text-white tabular-nums">{value}</span>
      <span className="text-xs text-slate-500 text-center leading-tight">{label}</span>
    </div>
  );
}

function PlayerStatsCard({ player, onClose }: { player: Player; onClose: () => void }) {
  const s = player.stats;
  const { t } = useLanguage();
  if (!s) return null;

  const ratingColor =
    s.matchRating >= 8
      ? 'hsl(142 71% 55%)'
      : s.matchRating >= 7
      ? 'hsl(25 95% 65%)'
      : 'hsl(222 15% 55%)';

  const ratingBg =
    s.matchRating >= 8
      ? 'hsl(142 71% 45% / 0.15)'
      : s.matchRating >= 7
      ? 'hsl(25 95% 53% / 0.12)'
      : 'hsl(222 25% 18%)';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.65)' }} onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 18%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold font-mono flex-shrink-0"
            style={{ background: 'hsl(222 25% 18%)', color: 'hsl(222 15% 65%)' }}
          >
            {player.number}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-white truncate">{player.name}</p>
            <p className="text-xs text-slate-400">{player.position}</p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-bold px-2 py-1 rounded-lg"
              style={{ background: ratingBg, color: ratingColor }}
            >
              {s.matchRating.toFixed(1)}
            </span>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Key stats */}
        <div className="px-4 py-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t.gameDetail.tournamentStats}</p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <StatBadge label={t.gameDetail.goals} value={s.goals} icon={<Target size={14} />} />
            <StatBadge label={t.gameDetail.assists} value={s.assists} icon={<Zap size={14} />} />
            <StatBadge label={t.gameDetail.apps} value={s.appearances} icon={<Calendar size={14} />} />
          </div>

          {/* Match performance */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t.gameDetail.matchPerformance}</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{t.gameDetail.minutesPlayed}</span>
              <span className="text-white font-medium tabular-nums">{s.minutesPlayed}&apos;</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{t.gameDetail.shotsOnTarget}</span>
              <span className="text-white font-medium tabular-nums">{s.shots} / {s.shotsOnTarget}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{t.gameDetail.passesAccuracy}</span>
              <span className="text-white font-medium tabular-nums">{s.passes} / {s.passAccuracy}%</span>
            </div>
            {/* Pass accuracy bar */}
            <div className="w-full rounded-full h-1.5 mt-1" style={{ background: 'hsl(222 25% 18%)' }}>
              <div
                className="h-1.5 rounded-full transition-all"
                style={{ width: `${s.passAccuracy}%`, background: 'hsl(210 100% 56%)' }}
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{t.gameDetail.tackles}</span>
              <span className="text-white font-medium tabular-nums">{s.tackles}</span>
            </div>
            {(s.yellowCards > 0 || s.redCards > 0) && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">{t.gameDetail.cards}</span>
                <div className="flex items-center gap-1.5">
                  {s.yellowCards > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-3 rounded-sm inline-block" style={{ background: 'hsl(45 93% 47%)' }} />
                      <span className="text-white font-medium tabular-nums">{s.yellowCards}</span>
                    </span>
                  )}
                  {s.redCards > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-3 rounded-sm inline-block bg-red-500" />
                      <span className="text-white font-medium tabular-nums">{s.redCards}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Match rating bar */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Star size={12} className="text-slate-400" />
            <span className="text-xs text-slate-400">{t.gameDetail.matchRating}</span>
            <span className="ml-auto text-xs font-bold" style={{ color: ratingColor }}>{s.matchRating.toFixed(1)}</span>
          </div>
          <div className="w-full rounded-full h-1.5" style={{ background: 'hsl(222 25% 18%)' }}>
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${(s.matchRating / 10) * 100}%`, background: ratingColor }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayerRow({ player, align, onSelect }: { player: Player; align: 'left' | 'right'; onSelect: (p: Player) => void }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer ${align === 'right' ? 'flex-row-reverse' : ''}`}
      onClick={() => onSelect(player)}
      title={`View ${player.name}'s stats`}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold font-mono flex-shrink-0"
        style={{ background: 'hsl(222 25% 18%)', color: 'hsl(222 15% 65%)' }}
      >
        {player.number}
      </span>
      <div className={`flex-1 min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
        <div className="flex items-center gap-1.5" style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
          <span className="text-sm font-medium text-white truncate hover:text-blue-400 transition-colors">{player.name}</span>
          {player.hasGoal && <span className="text-xs">⚽</span>}
          {player.hasYellow && <span className="w-2.5 h-3 rounded-sm inline-block flex-shrink-0" style={{ background: 'hsl(45 93% 47%)' }} />}
          {player.hasRed && <span className="w-2.5 h-3 rounded-sm inline-block flex-shrink-0 bg-red-500" />}
          {player.isSub && <span className="text-xs text-green-400">↑</span>}
        </div>
        <span className="text-xs text-slate-500">{player.position}</span>
      </div>
      {player.rating && (
        <span
          className="text-xs font-bold font-mono tabular-nums px-1.5 py-0.5 rounded flex-shrink-0"
          style={{
            background: player.rating >= 8 ? 'hsl(142 71% 45% / 0.15)' : player.rating >= 7 ? 'hsl(25 95% 53% / 0.12)' : 'hsl(222 25% 18%)',
            color: player.rating >= 8 ? 'hsl(142 71% 55%)' : player.rating >= 7 ? 'hsl(25 95% 65%)' : 'hsl(222 15% 55%)',
          }}
        >
          {player.rating.toFixed(1)}
        </span>
      )}
    </div>
  );
}

export default function MatchLineups() {
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [lineups, setLineups] = useState<ApiLineup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { t } = useLanguage();
  const searchParams = useSearchParams();
  const fixtureId = searchParams.get('id') ? Number(searchParams.get('id')) : null;

  const fetchLineups = useCallback(async () => {
    if (!fixtureId) {
      setLoading(false);
      return;
    }
    try {
      setError(false);
      const data = await getFixtureLineups(fixtureId);
      setLineups(data ?? []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [fixtureId]);

  useEffect(() => {
    fetchLineups();
  }, [fetchLineups]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</h3>
        </div>
        <div className="p-4 space-y-2 animate-pulse">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-2">
              <div className="flex-1 h-8 rounded-lg" style={{ background: 'hsl(222 25% 16%)' }} />
              <div className="flex-1 h-8 rounded-lg" style={{ background: 'hsl(222 25% 16%)' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</h3>
        </div>
        <div className="p-4 text-center">
          <p className="text-sm text-slate-500 mb-3">{t.dashboard.lineupsError}</p>
          <button
            onClick={() => { setLoading(true); fetchLineups(); }}
            className="flex items-center gap-1.5 mx-auto text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <RefreshCw size={12} />
            {t.common?.retry ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  // ── No fixture ID ─────────────────────────────────────────────────────────
  if (!fixtureId) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</h3>
        </div>
        <div className="p-6 text-center">
          <Users size={28} className="mx-auto text-slate-600 mb-2" />
          <p className="text-sm text-slate-500">{t.dashboard.lineupsNoFixture}</p>
        </div>
      </div>
    );
  }

  // ── No lineups yet released ───────────────────────────────────────────────
  if (lineups.length === 0) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</h3>
        </div>
        <div className="p-6 text-center">
          <Users size={28} className="mx-auto text-slate-600 mb-2" />
          <p className="text-sm text-slate-500">{t.dashboard.lineupsNotYetReleased}</p>
          <p className="text-xs text-slate-600 mt-1">{t.dashboard.lineupsCheckBack}</p>
        </div>
      </div>
    );
  }

  const homeLineupData = lineups[0];
  const awayLineupData = lineups[1];

  const homePlayers = homeLineupData ? mapApiLineupToPlayers(homeLineupData) : [];
  const awayPlayers = awayLineupData ? mapApiLineupToPlayers(awayLineupData) : [];

  return (
    <>
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Users size={15} className="text-slate-400" />
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.startingLineups}</h3>
        </div>

        {/* Formation header */}
        <div className="grid grid-cols-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <div className="px-5 py-2 flex items-center gap-2" style={{ borderRight: '1px solid hsl(222 25% 16%)' }}>
            {homeLineupData?.team?.logo ? (
              <img src={homeLineupData.team.logo} alt={homeLineupData.team.name} className="w-6 h-6 object-contain flex-shrink-0" />
            ) : null}
            <div>
              <p className="text-xs font-semibold text-white">{homeLineupData?.team?.name ?? '—'}</p>
              {homeLineupData?.formation && <p className="text-xs text-slate-500">{homeLineupData.formation}</p>}
            </div>
          </div>
          <div className="px-5 py-2 flex items-center gap-2 justify-end">
            <div className="text-right">
              <p className="text-xs font-semibold text-white">{awayLineupData?.team?.name ?? '—'}</p>
              {awayLineupData?.formation && <p className="text-xs text-slate-500">{awayLineupData.formation}</p>}
            </div>
            {awayLineupData?.team?.logo ? (
              <img src={awayLineupData.team.logo} alt={awayLineupData.team.name} className="w-6 h-6 object-contain flex-shrink-0" />
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 divide-x" style={{ divideColor: 'hsl(222 25% 16%)' }}>
          <div className="px-2 py-2 space-y-0.5">
            {homePlayers.map((p) => (
              <PlayerRow key={p.id} player={p} align="left" onSelect={setSelectedPlayer} />
            ))}
          </div>
          <div className="px-2 py-2 space-y-0.5">
            {awayPlayers.map((p) => (
              <PlayerRow key={p.id} player={p} align="right" onSelect={setSelectedPlayer} />
            ))}
          </div>
        </div>
      </div>

      {selectedPlayer && (
        <PlayerStatsCard player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
      )}
    </>
  );
}